/**
 * Browser-safe AI copy and labels (imported by src/app). Holds no real-cost figures: never import server/lib/ai-pricing.ts
 * from here or from the app.
 */

/** What the Free plan is ideal for, fixed. */
export const FREE_PLAN_CUSTOMERS = 50;

/** One-time top-up packs, price in paise. A pack's price is the amount charged (no GST is added); ₹X paid adds ₹X of usage credit. */
export const AI_TOPUP_PACKS_PAISE = [9_900, 24_900, 49_900] as const;

/** The named rule behind "about N active customers": an active customer gets this many AI actions in a month. */
export const ACTIVE_CUSTOMER_RULE = { replies: 10, followUps: 2, triage: 2, chatTurns: 1 } as const;


/** One sentence on how "ideal for up to N customers a month" is worked out, shown in small text under the plans. */
export const ESTIMATE_RULE_TEXT = `Based on measured usage: ${ACTIVE_CUSTOMER_RULE.replies} replies, ${ACTIVE_CUSTOMER_RULE.followUps} follow-ups, ${ACTIVE_CUSTOMER_RULE.triage} triage reads and ${ACTIVE_CUSTOMER_RULE.chatTurns} team-chat request per customer each month.`;

/** The top-up rule, stated wherever top-ups are sold. */
export const TOPUP_RULE_TEXT = "Top-up credit never expires. It is used only after the month's plan credit runs out, and stays if you change or cancel your plan. A top-up costs exactly the price shown; nothing is added on top.";

/** What each AI call was for. Shown in the usage breakdown and used to measure cost per action. */
export const aiPurposes = [
  "chat",
  "reply",
  "follow_up",
  "support_reply",
  "triage",
  "morning_report",
  "weekly_report",
  "payment_reminder",
  "ideas",
  "caption",
  "document",
  "summary",
  "transcription",
  "voice_note",
  "vision",
  "image",
  "lead_intel",
  "analytics",
  "lead_search",
  "lead_outreach",
] as const;
export type AiPurpose = (typeof aiPurposes)[number];

export const aiPurposeLabels: Readonly<Record<AiPurpose, string>> = {
  chat: "Team chat",
  reply: "Customer replies",
  follow_up: "Follow-ups",
  support_reply: "Support replies",
  triage: "Message triage",
  morning_report: "Morning report",
  weekly_report: "Weekly report",
  payment_reminder: "Payment reminders",
  ideas: "Post ideas",
  caption: "Captions",
  document: "Documents",
  summary: "Chat memory",
  transcription: "Customer voice notes",
  voice_note: "Your voice notes",
  vision: "Photos",
  image: "Post images",
  lead_intel: "Lead scoring",
  analytics: "Analytics takeaways",
  lead_search: "Lead searches",
  lead_outreach: "Messages to found leads",
};

/** Free plan credit as owners see it (2x the real cost the server allows): a verified email unlocks the trial, a connected channel the full amount. */
export const FREE_TRIAL_CREDIT_PAISE = 1_000;
export const FREE_FULL_CREDIT_PAISE = 4_000;
